import {
  Button,
  ButtonProps,
  Checkbox,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  Box,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  VStack,
  HStack,
  Spinner,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  CustomerInitiateRequest,
  CustomerInitiateResponse,
  ExpertisePackageGroup,
  FormBaseProps,
  CheckCampaignResponse,
} from 'types';
import React, { useRef } from 'react';
import * as yup from 'yup';
import { FocusableElement } from '@chakra-ui/utils';
import { useFormik } from 'formik';
import { AddIcon } from '@chakra-ui/icons';
import { CarEntryRow } from 'core/types/Types';
import { FormControlFormInput, FormControlFormPhoneInput, GenericMessages, NoSpecialCharacterRegex } from 'core';
import { useMutation } from 'react-query';
import { Api } from 'api';
import { ExpertisePackageGroupSelect, ExpertisePackageSelect } from 'components';
import { AdditionalPackageSelect } from 'components/AdditionalPackageSelect';
import { t, Trans } from '@lingui/macro';

export interface AddCarWithPlateFormValues {
  license: string;
  expertisePackage?: string;
  expertisePackageGroup?: string;
  additionalPackages?: number[];
  packageCode?: string;
  discountApplied?: boolean;
  discountedPrice?: string;
  authorizationDocumentNumber?: string;
  expertisePackagePrice?: string;
  additionalPackagesAvailable?: boolean;
  phoneNumber?: string;
  campaignApplied?: boolean;
  campaignId?: number;
  campaignName?: string;
  campaignDiscountRate?: number;
  campaignExpireDate?: Date;
}
export type AddCarWithPlateFormProps = FormBaseProps<AddCarWithPlateFormValues, CarEntryRow>;

const addCarWithPlateFormValidationSchema = yup.object({
  license: yup
    .string()
    .required(GenericMessages.CannotBeEmpty)
    .matches(NoSpecialCharacterRegex, GenericMessages.NoSpecialCharater),
  expertisePackageGroup: yup.number().required(GenericMessages.CannotBeEmpty),
  expertisePackage: yup.number().when('isPackageCodeShown', {
    is: false,
    then: yup.number().required(GenericMessages.CannotBeEmpty),
    otherwise: yup.number(),
  }),
  packageCode: yup.string().when('isPackageCodeShown', {
    is: true,
    then: yup
      .string()
      .required(GenericMessages.CannotBeEmpty)
      .matches(NoSpecialCharacterRegex, GenericMessages.NoSpecialCharater),
    otherwise: yup.string(),
  }),
  discountApplied: yup.boolean(),
  discountedPrice: yup.string().when('discountApplied', {
    is: true,
    then: yup.string().required(GenericMessages.CannotBeEmpty),
    otherwise: yup.string(),
  }),
  authorizationDocumentNumber: yup.string().when('expertisePackageGroup', {
    is: 10,
    then: yup.string().required(GenericMessages.CannotBeEmpty),
    otherwise: yup.string(),
  }),
  phoneNumber: yup.string(),
  campaignApplied: yup.boolean(),
});

export const AddCarWithPlateForm: React.FC<AddCarWithPlateFormProps> = ({
  defaultValues,
  formId,
  initialFocusRef,
  onSubmitBegin,
  onSubmitComplete,
  onSubmitFailed,
}) => {
  const [showPackageCodeInput, setShowPackageCodeInput] = React.useState(false);
  const [showDiscountCheckbox, setShowDiscountCheckbox] = React.useState(false);
  const [campaignData, setCampaignData] = React.useState<CheckCampaignResponse | null>(null);
  const [isCheckingCampaign, setIsCheckingCampaign] = React.useState(false);
  const [discountedPriceData, setDiscountedPriceData] = React.useState<{
    discountedPrice?: number;
    royaltyCalculatedPrice?: number;
  } | null>(null);
  const [selectedPackageGroup, setSelectedPackageGroup] = React.useState<ExpertisePackageGroup | null>(null);
  const { isOpen: isCampaignModalOpen, onOpen: onCampaignModalOpen, onClose: onCampaignModalClose } = useDisclosure();

  const toast = useToast();

  // Dark/Light mode colors
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const expireDateColor = useColorModeValue('gray.600', 'gray.300');

  const formik = useFormik({
    validateOnChange: false,
    initialValues: {
      license: defaultValues?.license || '',
      expertisePackage: defaultValues?.expertisePackage || '',
      expertisePackageGroup: defaultValues?.expertisePackageGroup || '',
      additionalPackages: defaultValues?.additionalPackages || [],
      packageCode: defaultValues?.packageCode || '',
      isPackageCodeShown: showPackageCodeInput,
      discountApplied: defaultValues?.discountApplied || false,
      discountedPrice: defaultValues?.discountedPrice || '',
      authorizationDocumentNumber: defaultValues?.authorizationDocumentNumber || '',
      expertisePackagePrice: defaultValues?.expertisePackagePrice || '',
      additionalPackagesAvailable: defaultValues?.additionalPackagesAvailable || true,
      phoneNumber: defaultValues?.phoneNumber || '',
      campaignApplied: defaultValues?.campaignApplied || false,
      campaignId: defaultValues?.campaignId || undefined,
      campaignName: defaultValues?.campaignName || '',
      campaignDiscountRate: defaultValues?.campaignDiscountRate || undefined,
      campaignExpireDate: defaultValues?.campaignExpireDate || undefined,
    },
    validationSchema: addCarWithPlateFormValidationSchema,
    onSubmit: (values) => {
      if (onSubmitBegin) {
        onSubmitBegin();
      }

      // Kampanya aktifse ve indirim uygulanmışsa phoneNumberForDiscount ekle
      const customerInitiateRequest: CustomerInitiateRequest = {
        license: values.license,
        expertisePackage: values.expertisePackage || '',
        expertisePackageGroup: values.expertisePackageGroup || '',
        packageCode: values.packageCode,
        discountApplied: values.discountApplied,
        discountedPrice: values.discountedPrice,
        additionalPackages: values.additionalPackages,
        authorizationDocumentNumber: values.authorizationDocumentNumber,
        phoneNumberForDiscount:
          values.campaignApplied && values.discountApplied && values.phoneNumber ? values.phoneNumber : undefined,
      };

      // eslint-disable-next-line no-use-before-define
      mutation.mutateAsync(customerInitiateRequest);
    },
  });

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.toUpperCase().replace(' ', '');
    formik.handleChange(e);
  };

  const discountedRoyaltyPriceMutation = useMutation<
    { message: string; showMessage: boolean; discountedPrice?: number; royaltyCalculatedPrice?: number },
    any,
    any
  >(async (values) => {
    const response = await Api.Customer.getDiscountedRoyaltyPrice({
      plate: values.license,
      expertisePackageGroupId: Number(values.expertisePackageGroup),
      expertisePackageId: Number(values.expertisePackage),
      packageCode: values.packageCode,
      discountApplied: values.discountApplied,
      discountedPrice: Number(values.discountedPrice),
      abilityDefinitionTypes: values.additionalPackages,
      phoneNumberForDiscount:
        values.campaignApplied && values.discountApplied && values.phoneNumber ? values.phoneNumber : undefined,
    });
    return response.data;
  });

  // Debounce ile indirimli fiyat değişikliklerini takip et
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      // İndirim uygulanmıyorsa sıfırla
      if (!formik.values.discountApplied) {
        setDiscountedPriceData(null);
        return;
      }

      // Only make API call if discount is applied
      // If campaign exists, check if it can be applied to this group
      const canApplyCampaign =
        !campaignData?.hasCampaign ||
        (selectedPackageGroup?.campaignAvailable !== false && selectedPackageGroup?.campaignAvailable !== undefined);

      if (
        formik.values.discountApplied &&
        formik.values.discountedPrice &&
        formik.values.license &&
        formik.values.expertisePackageGroup &&
        formik.values.expertisePackage &&
        canApplyCampaign
      ) {
        discountedRoyaltyPriceMutation.mutate(formik.values, {
          onSuccess: (data) => {
            setDiscountedPriceData({
              discountedPrice: data.discountedPrice,
              royaltyCalculatedPrice: data.royaltyCalculatedPrice,
            });
            // Backend'den gelen değeri input'a yazmıyoruz
            if (data.message !== '' && data.showMessage) {
              toast({
                title: data.message,
                status: 'info',
                isClosable: true,
              });
            }
          },
          onError: (error) => {
            console.error('Error getting discounted price:', error);
            setDiscountedPriceData(null);
          },
        });
      } else {
        setDiscountedPriceData(null);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [
    formik.values.discountedPrice,
    formik.values.discountApplied,
    formik.values.campaignApplied,
    formik.values.additionalPackages,
    formik.values.expertisePackage,
    formik.values.license,
    formik.values.expertisePackageGroup,
    formik.values.expertisePackage,
    campaignData,
    selectedPackageGroup,
    showDiscountCheckbox,
  ]);

  const campaignCheckMutation = useMutation<CheckCampaignResponse, any, string>(
    async (phoneNumber: string) => {
      setIsCheckingCampaign(true);
      const response = await Api.Reporting.checkCampaign(phoneNumber);
      return response.data;
    },
    {
      onSuccess: async (data) => {
        setCampaignData(data);
        if (data.hasCampaign) {
          // Check if the selected package group supports campaigns
          const canApplyCampaign = selectedPackageGroup?.campaignAvailable !== false;

          formik.setFieldValue('campaignApplied', canApplyCampaign);
          formik.setFieldValue('campaignId', data.campaignId);
          formik.setFieldValue('campaignName', data.campaignName);
          formik.setFieldValue('campaignDiscountRate', data.discountRate);
          formik.setFieldValue('campaignExpireDate', data.campaignExpireDate);

          // Only apply discount if campaign is available for this package group
          if (canApplyCampaign) {
            formik.setFieldValue('discountApplied', true);
          } else {
            formik.setFieldValue('discountApplied', false);
          }

          // Only calculate discounted price if campaign can be applied
          if (canApplyCampaign) {
            // Paket fiyatına göre indirimli fiyatı hesapla
            const packagePrice = parseFloat(formik.values.expertisePackagePrice || '0');
            const discountRate = data.discountRate || 0;
            const calculatedDiscountedPrice = packagePrice * (1 - discountRate / 100);

            // Hesaplanan değeri input'a yaz
            formik.setFieldValue('discountedPrice', calculatedDiscountedPrice.toFixed(2));
          }

          // API'den royalti hesaplaması için çağrı yap (sadece royalti bilgisi için)
          if (
            canApplyCampaign &&
            formik.values.license &&
            formik.values.expertisePackageGroup &&
            formik.values.expertisePackage
          ) {
            try {
              const packagePrice = parseFloat(formik.values.expertisePackagePrice || '0');
              const discountRate = data.discountRate || 0;
              const calculatedDiscountedPrice = packagePrice * (1 - discountRate / 100);
              const discountedPriceResponse = await discountedRoyaltyPriceMutation.mutateAsync({
                ...formik.values,
                campaignApplied: true,
                discountApplied: true,
                discountedPrice: calculatedDiscountedPrice.toString(),
              });

              if (discountedPriceResponse.royaltyCalculatedPrice) {
                setDiscountedPriceData({
                  discountedPrice: calculatedDiscountedPrice,
                  royaltyCalculatedPrice: discountedPriceResponse.royaltyCalculatedPrice,
                });
              }
              formik.setFieldValue('discountedPrice', calculatedDiscountedPrice.toString());
            } catch (error) {
              console.error('Error getting royalty calculation:', error);
            }
          }

          toast({
            title: 'Kampanya Bulundu!',
            description: `${data.campaignName} - ${data.discountRateStr} indirim`,
            status: 'success',
            isClosable: true,
          });
        } else {
          toast({
            title: 'Kampanya Bulunamadı',
            description: 'Bu telefon numarası için aktif kampanya bulunmuyor.',
            status: 'info',
            isClosable: true,
          });
        }
      },
      onError: (error) => {
        console.error('Campaign check error:', error);
        toast({
          title: 'Hata',
          description: error?.response?.data?.message || 'Kampanya sorgulanırken bir hata oluştu.',
          status: 'error',
          isClosable: true,
        });
      },
      onSettled: () => {
        setIsCheckingCampaign(false);
        // Sorgu tamamlanınca (başarı/hata) modalı kapat — böylece sorgu boyunca
        // modal açık kalır ve içindeki yüklenme spinner'ı görünür.
        onCampaignModalClose();
      },
    },
  );

  const mutation = useMutation<CustomerInitiateResponse, any, CustomerInitiateRequest>(
    async (customerInıtiateRequest: CustomerInitiateRequest) => {
      // Store campaign phone number for consistency check
      if (formik.values.campaignApplied && formik.values.phoneNumber) {
        sessionStorage.setItem('campaignPhoneNumber', formik.values.phoneNumber);
        sessionStorage.setItem(
          'campaignData',
          JSON.stringify({
            campaignId: formik.values.campaignId,
            campaignName: formik.values.campaignName,
            discountRate: formik.values.campaignDiscountRate,
            expireDate: formik.values.campaignExpireDate,
          }),
        );
      } else {
        sessionStorage.removeItem('campaignPhoneNumber');
        sessionStorage.removeItem('campaignData');
      }

      const response = await Api.Customer.initiateCustomer(customerInıtiateRequest);
      return response.data;
    },
    {
      onSuccess: (customerData) => {
        toast({
          title: t`Araç Başarılı Bir Şekilde Eklendi`,
          status: 'success',
          isClosable: true,
        });

        if (onSubmitComplete) {
          onSubmitComplete({
            id: customerData.savedCustomerId,
            hasHistoryByPlate: customerData.hasHistoryByPlate,
            histories: customerData.histories,
            license: formik.values.license,
          });
          if (customerData.fileStatus) {
            toast({
              title:
                customerData.fileStatus === 'Belge Geçerlidir'
                  ? customerData.fileStatus
                  : `Yetki Belgesi ${customerData.fileStatus}`,
              status: customerData.fileStatus === 'Belge Geçerlidir' ? 'success' : 'info',
              isClosable: true,
            });
          }
          formik.resetForm();
          // Form resetlendiğinde campaign state'lerini de temizle
          setCampaignData(null);
          setDiscountedPriceData(null);
        }
      },
      onError: (err) => {
        console.error(err);
        toast({
          title: err.response.data.message,
          status: 'error',
          isClosable: true,
        });
        if (onSubmitFailed) {
          onSubmitFailed();
        }
      },
    },
  );

  const handleCampaignQuery = () => {
    // Modal'ı aç
    onCampaignModalOpen();
  };

  const handlePhoneInputQuery = () => {
    const phoneNumber = formik.values.phoneNumber?.trim();
    if (phoneNumber && phoneNumber.length >= 10) {
      campaignCheckMutation.mutate(phoneNumber);
    }
  };

  const handlePackageGroupChange = (selectedGroup: ExpertisePackageGroup) => {
    // Store the selected package group
    setSelectedPackageGroup(selectedGroup);

    // eslint-disable-next-line no-console
    if (selectedGroup.externalPackageGroupSource !== null) {
      setShowPackageCodeInput(true);
      formik.setFieldValue('isPackageCodeShown', true);
      formik.setFieldValue('expertisePackage', '');
      formik.setFieldValue('discountApplied', false);
      formik.setFieldValue('discountedPrice', '');
      formik.setFieldValue('additionalPackages', []);
    } else {
      setShowPackageCodeInput(false);
      formik.setFieldValue('isPackageCodeShown', false);
      formik.setFieldValue('packageCode', '');
    }

    if (selectedGroup?.discountAvailable) {
      setShowDiscountCheckbox(true);
    } else {
      setShowDiscountCheckbox(false);
      formik.setFieldValue('discountApplied', false);
      formik.setFieldValue('discountedPrice', '');
    }

    if (selectedGroup.additionalPackagesAvailable) {
      formik.setFieldValue('additionalPackagesAvailable', true);
    } else {
      formik.setFieldValue('additionalPackagesAvailable', false);
    }

    // Reset selected package when changing package group
    formik.setFieldValue('expertisePackage', '');

    // Check if campaign can be applied to new package group
    if (campaignData?.hasCampaign) {
      if (selectedGroup.campaignAvailable === false || selectedGroup.campaignAvailable === undefined) {
        // Campaign exists but can't be applied to this group
        formik.setFieldValue('campaignApplied', false);
        formik.setFieldValue('discountApplied', false);
        formik.setFieldValue('discountedPrice', '');
        setDiscountedPriceData(null);
      } else {
        // Campaign can be applied to this group
        formik.setFieldValue('campaignApplied', true);
        // Only auto-apply discount if discountAvailable is true
        if (selectedGroup.discountAvailable) {
          formik.setFieldValue('discountApplied', true);
        }
      }
    }
  };

  return (
    <form onSubmit={formik.handleSubmit} id={formId}>
      <Flex direction="column" gap="4">
        <FormControlFormInput
          label={t`Plaka`}
          placeholder={t`Aracın Plakası`}
          name="license"
          onChange={onChange}
          value={formik.values.license}
          error={formik.errors.license != null}
          helperText={formik.errors.license}
          ref={initialFocusRef}
        />

        <Box>
          {/* İndirim Sorgula Butonu */}
          <Button size="sm" colorScheme="blue" onClick={handleCampaignQuery} width="full" variant="outline">
            {t`İndirim Sorgula`}
          </Button>

          {/* Kampanya Sonucu */}
          {campaignData?.hasCampaign && (
            <Alert status="success" mt={3} borderRadius="md" position="relative" pr={12}>
              <AlertIcon />
              <Box flex="1">
                <AlertDescription>
                  <VStack align="start" spacing={1}>
                    <HStack>
                      <Text fontWeight="bold">{campaignData.campaignName}</Text>
                      <Badge colorScheme="green" variant="solid">
                        {campaignData.discountRateStr} İNDİRİM
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" color={expireDateColor}>
                      {campaignData.campaignExpireDate &&
                        `${new Date(campaignData.campaignExpireDate).toLocaleDateString('tr-TR')}'e kadar geçerli`}
                    </Text>
                  </VStack>
                </AlertDescription>
              </Box>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="gray"
                position="absolute"
                right="4px"
                top="4px"
                minW="auto"
                h="20px"
                w="20px"
                p="0"
                fontSize="14px"
                fontWeight="bold"
                borderRadius="full"
                _hover={{ bg: 'gray.200' }}
                onClick={() => {
                  // Kampanyayı kaldır
                  setCampaignData(null);
                  setDiscountedPriceData(null);
                  formik.setFieldValue('campaignApplied', false);
                  formik.setFieldValue('discountedPrice', '');
                  formik.setFieldValue('discountApplied', false);
                  formik.setFieldValue('phoneNumber', '');
                }}
              >
                ×
              </Button>
            </Alert>
          )}
        </Box>
        <ExpertisePackageGroupSelect
          name="expertisePackageGroup"
          onChange={(event) => {
            formik.setFieldValue('additionalPackages', []);
            formik.handleChange(event);
          }}
          value={formik.values.expertisePackageGroup}
          error={formik.errors.expertisePackageGroup != null}
          helperText={formik.errors.expertisePackageGroup}
          onPackageGroupChange={handlePackageGroupChange}
        />

        {/* Campaign availability warning - shown after selecting package group */}
        {campaignData?.hasCampaign &&
          formik.values.expertisePackageGroup &&
          selectedPackageGroup &&
          (selectedPackageGroup.campaignAvailable === false ||
            selectedPackageGroup.campaignAvailable === undefined) && (
            <Alert status="warning" mt={2} borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                <Text fontSize="sm">Bu liste için indirim geçerli değil</Text>
              </AlertDescription>
            </Alert>
          )}
        {/* Esnaf paketi group id leri Autokale - Occocar için 10 - 71 */}
        {(formik.values.expertisePackageGroup === '10' || formik.values.expertisePackageGroup === '71') && (
          <FormControlFormInput
            label={t`Yetki Belgesi Numarası`}
            placeholder={t`Yetki Belgesi Numarası`}
            name="authorizationDocumentNumber"
            onChange={formik.handleChange}
            value={formik.values.authorizationDocumentNumber}
            error={formik.errors.authorizationDocumentNumber != null}
            helperText={formik.errors.authorizationDocumentNumber}
          />
        )}

        {!showPackageCodeInput && (
          <ExpertisePackageSelect
            name="expertisePackage"
            onChange={(e) => {
              const selectedOption = e.target.options[e.target.selectedIndex];
              formik.setFieldValue('expertisePackagePrice', selectedOption.getAttribute('data-price'));
              formik.setFieldValue('expertisePackage', e.target.value);

              // If campaign exists and group supports it, ensure discount is applied
              if (campaignData?.hasCampaign && selectedPackageGroup?.campaignAvailable === true) {
                formik.setFieldValue('campaignApplied', true);
                formik.setFieldValue('discountApplied', true);
              }
            }}
            value={formik.values.expertisePackage}
            error={formik.errors.expertisePackage != null}
            helperText={formik.errors.expertisePackage}
            isDisabled={!formik.values.expertisePackageGroup}
            expertisePackageGroupId={formik.values.expertisePackageGroup}
          />
        )}
        {formik.values.expertisePackage && formik.values.additionalPackagesAvailable && (
          <AdditionalPackageSelect
            expertisePackageId={Number(formik.values.expertisePackage)}
            onChange={(selectedIds) => {
              formik.setFieldValue('additionalPackages', selectedIds);
            }}
            formikValue={formik.values.additionalPackages}
          />
        )}

        {showPackageCodeInput && (
          <FormControlFormInput
            label={t`Kupon Kodu`}
            placeholder={t`Kupon Kodu`}
            name="packageCode"
            onChange={formik.handleChange}
            value={formik.values.packageCode}
            error={formik.errors.packageCode != null}
            helperText={formik.errors.packageCode}
          />
        )}

        {/* DISCOUNT */}
        {showDiscountCheckbox && !showPackageCodeInput && (
          <Checkbox
            size="sm"
            colorScheme="green"
            name="discountApplied"
            onChange={(event) => {
              formik.setFieldValue(event.target.name, event.target.checked);
            }}
            isChecked={formik.values.discountApplied}
          >
            {t`İndirim uygula`}
          </Checkbox>
        )}

        {formik.values.discountApplied && (
          <VStack align="start" spacing={3}>
            <FormControlFormInput
              size="xs"
              label=""
              placeholder={t`İndirimli Paket Fiyatı`}
              name="discountedPrice"
              onChange={onChange}
              value={formik.values.discountedPrice}
              error={formik.errors.discountedPrice != null}
              helperText={formik.errors.discountedPrice}
              width="auto"
            />
            {discountedPriceData && (
              <Box p={3} borderRadius="md" bg={useColorModeValue('gray.50', 'gray.700')} width="full">
                <VStack align="start" spacing={2}>
                  {discountedPriceData.discountedPrice && (
                    <HStack>
                      <Text fontSize="sm" fontWeight="medium" color={useColorModeValue('gray.700', 'gray.300')}>
                        İndirimli Toplam Tutar:
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" color={useColorModeValue('green.600', 'green.400')}>
                        {discountedPriceData.discountedPrice.toFixed(2)} ₺
                      </Text>
                    </HStack>
                  )}
                  {discountedPriceData.royaltyCalculatedPrice && (
                    <HStack>
                      <Text fontSize="sm" fontWeight="medium" color={useColorModeValue('gray.700', 'gray.300')}>
                        Royalti Hesaplanan Tutar:
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" color={useColorModeValue('blue.600', 'blue.400')}>
                        {discountedPriceData.royaltyCalculatedPrice.toFixed(2)} ₺
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
            )}
          </VStack>
        )}

        {/* Kampanya Sorgulama Modal'ı */}
        <Modal isOpen={isCampaignModalOpen} onClose={onCampaignModalClose} isCentered size="md">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t`Kampanya Sorgulama`}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color={secondaryTextColor}>
                  Kampanya sorgulamak için telefon numaranızı giriniz:
                </Text>
                <FormControlFormPhoneInput
                  label={t`Telefon Numarası`}
                  placeholder={t`Telefon Numarası`}
                  name="phoneNumber"
                  onChange={formik.handleChange}
                  value={formik.values.phoneNumber}
                  error={formik.errors.phoneNumber != null}
                  helperText={formik.errors.phoneNumber}
                />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <HStack spacing={3}>
                <Button variant="ghost" onClick={onCampaignModalClose}>
                  {t`İptal`}
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handlePhoneInputQuery}
                  isLoading={isCheckingCampaign}
                  isDisabled={!formik.values.phoneNumber?.trim() || formik.values.phoneNumber.length < 10}
                  leftIcon={isCheckingCampaign ? <Spinner size="sm" /> : undefined}
                >
                  {t`İndirim Sorgula`}
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    </form>
  );
};
export interface AddCarWithPlateModalButtonProps extends AddCarWithPlateFormProps {
  buttonProps?: ButtonProps;
}

export const AddCarWithPlateModalButton: React.FC<AddCarWithPlateModalButtonProps> = ({
  buttonProps,
  ...addCarWithPlateFormProps
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialFocusRef = useRef<FocusableElement>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const onFormSubmitBeing = () => {
    setIsLoading(true);
  };

  const onSubmitComplete = (value: CarEntryRow) => {
    setIsLoading(false);
    onClose();
    if (addCarWithPlateFormProps.onSubmitComplete) {
      addCarWithPlateFormProps.onSubmitComplete(value);
    }
  };

  const onSubmitFailed = () => {
    setIsLoading(false);
  };

  return (
    <>
      <Button width="100%" leftIcon={<AddIcon />} flexShrink="0" onClick={onOpen} {...buttonProps}>
        <Trans>Ekle</Trans>
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        initialFocusRef={initialFocusRef}
        isCentered
        size="lg"
        closeOnOverlayClick={!isLoading}
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(1px)" />
        <ModalContent
          borderRadius="xl"
          boxShadow="2xl"
          bg={useColorModeValue('white', 'gray.800')}
          border="1px solid"
          borderColor={useColorModeValue('gray.200', 'gray.600')}
          mx={4}
          my={4}
          maxH="90vh"
          overflow="hidden"
        >
          <ModalHeader
            borderTopRadius="xl"
            py={6}
            px={8}
            borderBottom="1px solid"
            borderColor={useColorModeValue('gray.200', 'gray.600')}
          >
            <Text fontSize="lg" fontWeight="semibold" color={useColorModeValue('gray.800', 'gray.100')}>
              <Trans>Araç Ekle</Trans>
            </Text>
          </ModalHeader>

          <ModalCloseButton
            isDisabled={isLoading}
            bg={useColorModeValue('gray.100', 'gray.700')}
            _hover={{
              bg: useColorModeValue('gray.200', 'gray.600'),
            }}
            borderRadius="full"
            top={4}
            right={4}
          />

          <ModalBody
            px={6}
            py={5}
            overflowY="auto"
            maxH="calc(90vh - 160px)"
            sx={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: useColorModeValue('gray.100', 'gray.700'),
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: useColorModeValue('gray.300', 'gray.500'),
                borderRadius: '3px',
              },
            }}
          >
            <AddCarWithPlateForm
              {...addCarWithPlateFormProps}
              formId="addCarWithPlateForm"
              initialFocusRef={initialFocusRef}
              onSubmitBegin={onFormSubmitBeing}
              onSubmitComplete={onSubmitComplete}
              onSubmitFailed={onSubmitFailed}
            />
          </ModalBody>

          <ModalFooter
            borderBottomRadius="xl"
            borderTop="1px solid"
            borderColor={useColorModeValue('gray.200', 'gray.600')}
            px={6}
            py={4}
            display="flex"
            justifyContent="flex-end"
            gap={3}
          >
            <Button isDisabled={isLoading} onClick={onClose} variant="outline" size="md">
              <Trans>İptal</Trans>
            </Button>
            <Button
              colorScheme="blue"
              isLoading={isLoading}
              type="submit"
              form="addCarWithPlateForm"
              loadingText="Ekleniyor..."
              size="md"
            >
              <Trans>Ekle</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// Legacy export for backward compatibility
export const AddCarWithPlatePopoverButton = AddCarWithPlateModalButton;
